import './addCourseContent.scss'
import { FieldArray, Form, Formik } from 'formik'
import * as Yup from 'yup'
import useScreenSize from '../../hooks/useScreenSize'
import Button from '../../components/button/Button'
import InputWithoutBorder from '../../components/input-field-no-border/InputWithoutBorder'
import AddCourseTopic from './AddCourseTopic'
import deleteIcon from '../../assets/icons/deleteFill.svg'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { UI_CONSTANT, ROUTES_PATH } from '../../utility/constants'
import BreadCrumbs from '../../components/breadcrumb/Breadcrumb'
import { useDispatch, useSelector } from 'react-redux'
import {
  createCourseContentModules,
  deleteModulesAndTopics,
  editCourseContent,
  getCourseByCourseId,
} from '../../store/builders/course/course.builder'
import { setCourseContent } from '../../store/slice/course/course.slice'
import Loader from '../../components/loader/Loader'
import {
  MODULE_NAME_MAX_LENGTH,
  TOPIC_NAME_MAX_LENGTH,
  VALIDATION_MESSAGES,
} from '../../utility/constantValidationMessages'

function AddCourseContent() {
  const dispatch = useDispatch()
  const screenSize = useScreenSize()
  const [initialValues, setInitialValues] = useState({
    modules: [{ moduleName: '', topic: [{ topicName: '', pdfFiles: [], videoFiles: [], mcqFile: [] }] }],
  })
  const { courseId, courseContent, currentCourse, loading, courseContentLoading } = useSelector((store) => store.course)
  const navigate = useNavigate()
  const { id } = useParams()

  const handleModuleDelete = (moduleIndex, values, setFieldValue, moduleId) => {
    const updatedFiles = values.modules.filter((_, index) => index !== moduleIndex)
    setFieldValue('modules', updatedFiles)
    id && dispatch(deleteModulesAndTopics({ id: moduleId, type: 'module' }))
  }

  const fetchCourseContent = useCallback(() => {
    if (id) {
      dispatch(getCourseByCourseId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    fetchCourseContent()
  }, [fetchCourseContent])

  const checkCourseContent = useCallback(() => {
    if (id && currentCourse?.courseDetails?.length) {
      const result = {
        modules: currentCourse?.courseDetails.map((module) => ({
          moduleId: module.moduleId,
          moduleName: module.moduleName,
          topic: module.course_modules_topics.map((topic) => ({
            topicId: topic?.topicId,
            topicName: topic?.topicName,
            pdfFiles: topic?.documentUrl
              ? JSON.parse(topic.documentUrl)
                  .filter((file) => file?.url?.includes('.pdf') || file?.url?.includes('.zip'))
                  .map((file) => ({ name: file.originalPath, s3Url: file?.url }))
              : [],
            videoFiles: topic?.videoUrl
              ? JSON.parse(topic?.videoUrl).map((video) => ({ name: video?.originalUrl, s3Url: video?.url }))
              : [],
            mcqFile: topic?.documentUrl
              ? JSON.parse(topic.documentUrl)
                  .filter((file) => file?.url?.includes('.xlsx'))
                  .map((file) => ({ name: file?.originalPath , s3Url: file?.url}))
              : [],
          })),
        })),
      }
      setInitialValues(result)
    } else if (Object.keys(courseContent)?.length && !id) {
      setInitialValues(courseContent)
    } else if (currentCourse?.courseDetails?.length === 0) {
      setInitialValues({
        modules: [{ moduleName: '', topic: [{ topicName: '', pdfFiles: [], videoFiles: [], mcqFile: [] }] }],
      })
    }
  }, [id, courseContent, currentCourse?.courseDetails])
  useEffect(() => {
    checkCourseContent()
  }, [id, courseContent, currentCourse?.courseDetails])

  const AddCourseSchema = Yup.object().shape({
    modules: Yup.array().of(
      Yup.object().shape({
        moduleName: Yup.string()
          .required(VALIDATION_MESSAGES.MODULE_NAME_REQUIRED)
          .max(MODULE_NAME_MAX_LENGTH, VALIDATION_MESSAGES.MODULE_NAME_LENGTH_VALIDATION),
        topic: Yup.array().of(
          Yup.object().shape({
            topicName: Yup.string()
              .required(VALIDATION_MESSAGES.TOPIC_NAME_REQUIRED)
              .max(TOPIC_NAME_MAX_LENGTH, VALIDATION_MESSAGES.TOPIC_NAME_LENGTH_VALIDATION),
            pdfFiles: Yup.array().of(Yup.mixed().nullable()),
            videoFiles: Yup.array().of(Yup.mixed().nullable()),
            mcqFile: Yup.array().of(Yup.mixed().nullable()),
          }),
        ),
      }),
    ),
  })

  const breadCrumbsArr = id
    ? [
        { title: id ? UI_CONSTANT.COURSE.EDIT_COURSE : UI_CONSTANT.COURSE.ADD_COURSE, link: null },
        { title: UI_CONSTANT.DASHBOARD, link: ROUTES_PATH.DASHBOARD },
        { title: UI_CONSTANT.COURSE.LABLE_COURSE, link: ROUTES_PATH.COURSE },
        {
          title: id ? UI_CONSTANT.COURSE.EDIT_COURSE_CONTENT : UI_CONSTANT.COURSE.COURSE_CONTENT,
          link: id ? ROUTES_PATH.EDIT_COURSE_CONTENT.replace(':id', id) : ROUTES_PATH.ADD_COURSE_CONTENT,
        },
      ]
    : [
        { title: id ? UI_CONSTANT.COURSE.EDIT_COURSE : UI_CONSTANT.COURSE.ADD_COURSE, link: null },
        { title: UI_CONSTANT.DASHBOARD, link: ROUTES_PATH.DASHBOARD },
        { title: UI_CONSTANT.COURSE.LABLE_COURSE, link: ROUTES_PATH.COURSE },
        { title: UI_CONSTANT.COURSE.COURSE_INFORMATION, link: ROUTES_PATH.ADD_COURSE_INFORMATION },
        {
          title: id ? UI_CONSTANT.COURSE.EDIT_COURSE_CONTENT : UI_CONSTANT.COURSE.COURSE_CONTENT,
          link: id ? ROUTES_PATH.EDIT_COURSE_CONTENT.replace(':id', id) : ROUTES_PATH.ADD_COURSE_CONTENT,
        },
      ]

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={AddCourseSchema}
        validate={(values) => {
        }}
        onSubmit={async (values, { setSubmitting }) => {
          dispatch(setCourseContent(values))
          if (id) {
            dispatch(editCourseContent({ ...values, courseId: id, navigate, to: 'courseList', dispatch }))
          } else if (courseId && !id) {
            dispatch(editCourseContent({ ...values, courseId, navigate, to: 'coursePricing', dispatch }))
          } else {
            dispatch(createCourseContentModules({ ...values, courseId, editNavigate: true, navigate, dispatch }))
          }
          setSubmitting(false)
        }}
      >
        {({ values, setFieldValue, handleSubmit, isValid, dirty, validateForm, setTouched, errors }) => (
          <div className={`add-course-list ${screenSize}`}>
            {courseContentLoading && <Loader loading={courseContentLoading} />}
            <div className='filter-bar-wraper-sticky'>
              <div className='course-course-div'></div>
              <div className='add-course-content-subheader'>
                <BreadCrumbs breadCrumbsArr={breadCrumbsArr} />
                <div>
                  <Button className={'course-content-draft-btn'} disabled={!(isValid && dirty)} onClick={{}}>
                    {UI_CONSTANT.SAVE_AS_DRAFT}
                  </Button>
                  <Button type='submit' className={'course-content-next-btn'} onClick={handleSubmit} disabled={loading}>
                    {id ? UI_CONSTANT.SAVE : UI_CONSTANT.NEXT}
                  </Button>
                </div>
              </div>
            </div>

            <Form>
              <div className='add-course-content-group-parent'>
                <b className='course-information-heading'>{UI_CONSTANT.COURSE.COURSE_CONTENT}</b>
                <p className='course-info-subHeading'>{UI_CONSTANT.COURSE.FORM_DESCRIPTION}</p>
                <FieldArray name='modules'>
                  {({ push }) => (
                    <>
                      <FieldArray name={`topic`}>
                        {() => (
                          <>
                            {values.modules.map((module, moduleIndex) => {
                              return (
                                <div className='main-div-course' key={moduleIndex}>
                                  <InputWithoutBorder
                                    className={'add-modal-name-input'}
                                    name={`modules[${moduleIndex}].moduleName`}
                                    inputTitle={'Module:'}
                                    placeholder={'Enter module name...'}
                                    required={true}
                                  >
                                    {values.modules.length > 1 && (
                                      <div className='course-module-delete-icon'>
                                        <img
                                          src={deleteIcon}
                                          onClick={() =>
                                            handleModuleDelete(moduleIndex, values, setFieldValue, module?.moduleId)
                                          }
                                        />
                                      </div>
                                    )}
                                  </InputWithoutBorder>
                                  <div className='course-content-divider' />

                                  <FieldArray name={`modules[${moduleIndex}].topic`}>
                                    {({ push, remove }) => (
                                      <>
                                        {module.topic.map((_, index) => (
                                          <AddCourseTopic
                                            key={index}
                                            index={index}
                                            moduleIndex={moduleIndex}
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            push={push}
                                            remove={remove}
                                          />
                                        ))}
                                        <div className='addTopic-btn-div'>
                                          <Button
                                            type='button'
                                            customClassName={'addTopic-btn-div'}
                                            onClick={() => {
                                              push({
                                                topicName: '',
                                                pdfFiles: [],
                                                videoFiles: [],
                                                mcqFile: [],
                                              })
                                            }}
                                          >
                                            <u>{UI_CONSTANT.COURSE.ADD_NEW_TOPIC}</u>
                                          </Button>
                                        </div>
                                      </>
                                    )}
                                  </FieldArray>
                                </div>
                              )
                            })}
                          </>
                        )}
                      </FieldArray>

                      <Button
                        type='button'
                        customClassName='module-add-btn'
                        onClick={async () => {
                          const errors = await validateForm()

                          setTouched({
                            modules: values.modules.map((_, index) => ({
                              moduleName: true,
                              topic: values.modules[index].topic.map(() => ({
                                topicName: true,
                              })),
                            })),
                          })

                          const hasErrors = values.modules.some(
                            (module, index) => errors.modules && errors.modules[index],
                          )
                          if (!hasErrors) {
                            push({
                              moduleName: '',
                              topic: [{ topicName: '', pdfFiles: [], videoFiles: [], mcqFile: [] }],
                            })
                          }
                        }}
                      >
                        {UI_CONSTANT.COURSE.ADD_MODULE}
                      </Button>
                    </>
                  )}
                </FieldArray>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </>
  )
}

export default AddCourseContent
